import { useRef } from 'react'

type FormatterTypes = 'money' | 'date'

type MoneyFormatterOptions = Omit<
  Intl.NumberFormatOptions,
  'style' | 'currency' | 'maximumFractionDigits' | 'minimumFractionDigits'
> & {
  fractions: 'always' | 'only-floats'
}
type MoneyFormatterReturn = { formatFn: (amount: number) => string; formatter: Intl.NumberFormat }

type DateFormatterOptions = Intl.DateTimeFormatOptions & { showHours: boolean }
type DateFormatterReturn = { formatFn: (date: Date | number) => string; formatter: Intl.DateTimeFormat }

type Options<T extends FormatterTypes> = T extends 'money' ? MoneyFormatterOptions : DateFormatterOptions
type Return<T extends FormatterTypes> = T extends 'money' ? MoneyFormatterReturn : DateFormatterReturn

/**
 * Custom hook that provides formatting utilities to strings using the
 *
 * @param options {Options} options object for the formatter inferred by the generic type `T`
 * @returns  A formatting function and the specific formatter that it uses for more specific use cases
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
 */
const useFormatter = <T extends FormatterTypes>(options: Options<T>): Return<T> => {
  const returnRef = useRef<Return<T>>(null)

  if (returnRef.current) return returnRef.current

  if ('fractions' in options)
    returnRef.current = {
      formatter: new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        ...options,
      }),
      formatFn: (arg) =>
        options.fractions === 'always' || !Number.isInteger(arg)
          ? returnRef.current.formatter.format(arg)
          : (returnRef.current.formatter as Intl.NumberFormat)
              .formatToParts(arg)
              .reduce((acc, { value }, i, arr) => (arr.length - i > 2 ? acc + value : acc), ''),
    } as Return<T>

  if ('showHours' in options)
    returnRef.current = {
      formatter: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        ...(options.showHours ? { hour: 'numeric', minute: 'numeric' } : {}),
        ...options,
      }),
      formatFn: (arg) => returnRef.current.formatter.format(arg),
    } as Return<T>

  return returnRef.current
}

export default useFormatter
